import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Images, Film, Play, X, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['all', 'mariage', 'festival', 'concert', 'sport', 'corporate', 'club', 'produit'];

const resolveUrl = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

const mediaTitle = (item, lang) => {
  const map = { fr: item.title, en: item.titleEn, ar: item.titleAr, it: item.titleIt };
  return map[lang] || item.title || '';
};

const youtubeThumb = (id) => `https://img.youtube.com/vi/${id}/sddefault.jpg`;

const Gallery = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('photo');
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get('categorie') || 'all');
  const [active, setActive] = useState(null);
  const [limit, setLimit] = useState(24);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/public/media`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => items.filter((i) => i.type === tab && (category === 'all' || i.category === category)),
    [items, tab, category]
  );
  const visible = useMemo(() => filtered.slice(0, limit), [filtered, limit]);

  useEffect(() => { setLimit(24); }, [tab, category]);

  const photoSchema = useMemo(() => {
    const photos = items.filter((i) => i.type === 'photo').slice(0, 24);
    if (!photos.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: 'Party LED — Galerie',
      url: 'https://www.party-led.com/galerie',
      image: photos.map((p) => resolveUrl(p.url)),
    };
  }, [items]);

  return (
    <div className="min-h-screen bg-black pt-24 pb-20" data-testid="gallery-page">
      <>
        <title>{`${t.gallery.title} | Party LED — Photos & Vidéos Bracelets LED`}</title>
        <meta name="description" content={t.gallery.seo} />
        <link rel="canonical" href="https://www.party-led.com/galerie" />
        <meta property="og:title" content={`${t.gallery.title} | Party LED`} />
        <meta property="og:description" content={t.gallery.seo} />
        <meta property="og:url" content="https://www.party-led.com/galerie" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {photoSchema && <script type="application/ld+json">{JSON.stringify(photoSchema)}</script>}
      </>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <p className="text-purple-400 text-xs uppercase tracking-[0.25em] mb-3">Party LED</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.gallery.title}
          </h1>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl">{t.gallery.subtitle}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="gallery-tabs">
          {[
            { key: 'photo', label: t.gallery.photos, icon: Images },
            { key: 'video', label: t.gallery.videos, icon: Film },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                tab === key
                  ? 'bg-purple-600 text-white shadow-[0_0_24px_rgba(147,51,234,0.35)]'
                  : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
              }`}
              data-testid={`gallery-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Category filters */}
        <div className="flex gap-2 flex-wrap mb-10" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all duration-200 ${
                category === c
                  ? 'bg-white text-black'
                  : 'bg-transparent text-gray-500 border border-white/10 hover:text-white hover:border-white/30'
              }`}
              data-testid={`gallery-filter-${c}`}
            >
              {c === 'all' ? t.gallery.all : t.gallery[c]}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-24" data-testid="gallery-loading">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <p className="text-gray-500 py-24 text-center" data-testid="gallery-empty">{t.gallery.empty}</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {visible.map((item, idx) => {
              const title = mediaTitle(item, lang);
              const preview =
                item.type === 'video'
                  ? (item.thumbnail ? resolveUrl(item.thumbnail) : (item.source === 'youtube' ? youtubeThumb(item.url) : ''))
                  : resolveUrl(item.url);
              return (
                <button
                  key={item.id}
                  onClick={() => setActive(item)}
                  className="group relative aspect-square overflow-hidden rounded-xl bg-gray-900 border border-white/5 hover:border-purple-500/50 transition-all duration-300 text-left"
                  data-testid={`gallery-item-${item.id}`}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt={title || 'Party LED'}
                      loading={idx < 8 ? 'eager' : 'lazy'}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  ) : item.type === 'video' && item.source !== 'youtube' ? (
                    <video
                      src={`${resolveUrl(item.url)}#t=0.6`}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-all duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-8 h-8 text-gray-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-80" />
                  {item.type === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-purple-600/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      </span>
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-xs md:text-sm font-medium leading-snug">{title}</p>
                    <p className="text-purple-400/80 text-[10px] uppercase tracking-wider mt-0.5">
                      {t.gallery[item.category] || item.category}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {visible.length < filtered.length && (
          <div className="flex justify-center mt-10">
            <button
              onClick={() => setLimit((l) => l + 24)}
              className="px-8 py-3 rounded-full border border-white/15 text-gray-300 text-sm hover:text-white hover:border-purple-500/60 transition-colors"
              data-testid="gallery-load-more"
            >
              {t.gallery.loadMore || 'Voir plus'} ({filtered.length - visible.length})
            </button>
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-white/5">
          <ShareButtons path="/galerie" title={`${t.gallery.title} — Party LED`} />
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setActive(null)}
            aria-label={t.gallery.close}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {active.type === 'photo' ? (
              <img loading="lazy"
                src={resolveUrl(active.url)}
                alt={mediaTitle(active, lang)}
                className="w-full max-h-[80vh] object-contain rounded-xl"
                data-testid="lightbox-photo"
              />
            ) : active.source === 'youtube' ? (
              <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden">
                <iframe
                  title={mediaTitle(active, lang) || 'Party LED'}
                  src={`https://www.youtube.com/embed/${active.url}?autoplay=1&rel=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  data-testid="lightbox-youtube"
                />
              </div>
            ) : (
              <video
                src={resolveUrl(active.url)}
                poster={active.thumbnail ? resolveUrl(active.thumbnail) : undefined}
                controls
                autoPlay
                className="w-full max-h-[80vh] rounded-xl bg-black"
                data-testid="lightbox-video"
              >
                <track kind="captions" />
              </video>
            )}
            <p className="text-white text-center mt-4 text-sm">{mediaTitle(active, lang)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
